import React, { useEffect, useState } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";

import { Button, FormButton } from "@components/buttons";
import SearchInputField from "@components/inputs/shared/SearchInputField";
import { useRootAppStore } from "@hooks";
import { SearchAction } from "@state/stores/offer/search/SearchStore";
import { WebPlatform } from "@state/stores/types";
import { getCurrentCulture, isCasinoHeader } from "@utils";
import { observer } from "mobx-react";

import HeaderSuggestedSearchList from "./search/HeaderSuggestedSearchPopup";

export default observer(function Search() {
	const [isHover, setIsHover] = useState(false);

	const { pathname } = useLocation();

	const rootStore = useRootAppStore<WebPlatform>();

	const searchStore = rootStore.searchStore;

	const showSearchIcon =
		pathname.includes(`/${getCurrentCulture()}/home`) ||
		pathname.includes(`/${getCurrentCulture()}/live`) ||
		pathname.includes(`/${getCurrentCulture()}/sports`) ||
		pathname.includes(`/${getCurrentCulture()}/search`);

	const methods = useForm({
		mode: "onChange",
		defaultValues: {
			search: searchStore.searchTerm,
		},
	});

	const onChange: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
		e.preventDefault();
		e.stopPropagation();
		const code = e.which;
		if (code === 13) {
			if (searchStore.searchValidation.test(searchStore.searchTerm)) {
				searchStore.setIsSuggestedListOpen(false);
				const searchResult = searchStore.onSubmit({
					action: SearchAction.SEARCH,
					period: "full",
				});

				if (searchResult) {
					methods.reset();
				}

				searchStore.onReset();
			}
		} else if (code === 27) {
			searchStore.resetSearch();
		}
	};

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		searchStore.onTermChange(e.target.value);
		if (
			e.target.value.length >= searchStore.minLengthForFilterSuggestedList
		) {
			searchStore.setIsSuggestedListOpen(true);
		} else {
			searchStore.setIsSuggestedListOpen(false);
		}
	};

	const onSuccess = (
		data: { search: string },
		event: React.MouseEvent<HTMLFormElement>
	) => {
		event.stopPropagation();
		event.preventDefault();
		const { search } = data;

		searchStore.onTermChange(search);

		const searchResult = searchStore.onSubmit(
			{ action: SearchAction.SEARCH, period: "full" },
			null
		);

		if (searchResult) {
			methods.reset();
		}

		methods.resetField("search", {
			defaultValue: searchStore.searchTerm,
		});
	};

	useEffect(() => {
		const fetchSuggestedList = async () => {
			await searchStore.getSuggestedSearchList();
		};

		if (searchStore.isSearchOpen) {
			setIsHover(false);
			fetchSuggestedList();
		}
	}, [searchStore.isSearchOpen]);

	useEffect(() => {
		if (
			!rootStore.searchBarViewStore.searchTerm &&
			methods.getValues("search")
		) {
			methods.reset({ search: "" });
		}
	}, [rootStore.searchBarViewStore.searchTerm]);

	if (isCasinoHeader() || !showSearchIcon) {
		return null;
	}

	const shouldDisplayPopup =
		searchStore.filteredSearchList?.length ||
		methods.getValues("search")?.length > 2 ||
		methods.formState.errors.search != null;

	if (searchStore.isSearchOpen) {
		return (
			<>
				<FormProvider {...methods}>
					<form
						onSubmit={methods.handleSubmit(onSuccess)}
						className="searchbar is-active"
					>
						<SearchInputField
							inputClassName="searchbar__input input"
							customErrorClassName="searchbar__validation message--validation field-validation-error"
							name="search"
							validate={(value: string | undefined) => {
								if (value == null) {
									return "";
								}
								if (value.trim().length < 3) {
									return "SEARCH.INFO";
								}

								return (
									searchStore.searchValidation.test(value) ||
									"SEARCH.INFO"
								);
							}}
							onKeyUp={onChange}
							onChange={handleOnChange}
							onClick={() => {
								searchStore.setIsSuggestedListOpen(true);
							}}
							defaultValue={methods.getValues("search") || ""}
							placeholder="HEADER.SEARCH_PLACEHOLDER"
							autoComplete="off"
							autoFocus
						/>
						<FormButton
							className="searchbar__btn btn--reset btn--ghost"
							iconOnly={true}
							iconClassName="u-icon--sml u-icon--search--alt u-color--text--primary"
						/>
						<Button
							type="button"
							className="searchbar__close btn--reset btn--ghost"
							iconOnly={true}
							iconClassName="u-icon--tny u-icon--close"
							onClick={() => {
								searchStore.setIsSearchOpen(false);
								setIsHover(false);
							}}
						/>
					</form>

					{shouldDisplayPopup && <HeaderSuggestedSearchList />}
				</FormProvider>
			</>
		);
	}

	return (
		<Button
			className="header__search btn--reset btn--ghost"
			iconOnly={true}
			iconClassName={
				"header__search__icon u-icon u-icon--sml u-icon--search--alt" +
				(isHover ? " u-color--brand--primary" : " u-color--core--snow")
			}
			onClick={searchStore.searchButtonOnClick}
			onMouseEnter={() => setIsHover(true)}
			onMouseLeave={() => setIsHover(false)}
		/>
	);
});
